import { faCars, faDash, faUserGroup } from "@fortawesome/pro-light-svg-icons";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TierDetails, TierLevel } from "typings/Tier";
import { formatCurrency } from "utils/formatCurrency";

export const ENTHUSIAST_INCLUDED_SLOTS = 5;
export const ENTHUSIAST_SLOTS_LIMIT = 10;
export const ENTHUSIAST_SEATS_LIMIT = 0;

export const PRO_INCLUDED_SLOTS = 10;
export const PRO_INCLUDED_SEATS = 2;

const ADDITIONAL_VEHICLE_SLOT_PRICE = 1;
const ADDITIONAL_COLLABORATOR_SEAT_PRICE = 5;

export const SUBSCRIPTION_CURRENCY = "USD";

type Allowance = {
  seats: number;
  slots: number;
};

const ALLOWANCE_BY_TIER_VALUE: Partial<Record<TierLevel, Allowance>> = {
  [TierLevel.ENTHUSIAST]: {
    seats: 0,
    slots: 5,
  },
  [TierLevel.PRO]: {
    seats: 2,
    slots: 10,
  },
};

export const useTiers = (): TierDetails[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        additional: [
          {
            title: t("tier.additional.vehicleSlot", {
              price: `${formatCurrency(ADDITIONAL_VEHICLE_SLOT_PRICE, SUBSCRIPTION_CURRENCY, true, false)} ${SUBSCRIPTION_CURRENCY}`,
            }),
          },
        ],
        additionalVehicleSlotPrice: ADDITIONAL_VEHICLE_SLOT_PRICE,
        basePrice: 4.99,
        features: [
          {
            iconProps: { flip: "horizontal", icon: faCars },
            title: t("tier.features.includedVehicles", {
              count: ALLOWANCE_BY_TIER_VALUE[TierLevel.ENTHUSIAST]?.slots,
            }),
          },
          {
            iconProps: { icon: faDash },
            title: t("tier.features.vehicleLimit", {
              count: ENTHUSIAST_SLOTS_LIMIT,
            }),
          },
        ],
        id: "enthusiast",
        level: TierLevel.ENTHUSIAST,
        productId: import.meta.env.REACT_APP_STRIPE_ENTHUSIAST_PRODUCT_ID,
        title: t("tier.enthusiast"),
      },
      {
        additional: [
          {
            title: t("tier.additional.vehicleSlot", {
              price: `${formatCurrency(ADDITIONAL_VEHICLE_SLOT_PRICE, SUBSCRIPTION_CURRENCY, true, false)} ${SUBSCRIPTION_CURRENCY}`,
            }),
          },
          {
            title: t("tier.additional.collaboratorSeat", {
              price: `${formatCurrency(
                ADDITIONAL_COLLABORATOR_SEAT_PRICE,
                SUBSCRIPTION_CURRENCY,
                true,
                false,
              )} ${SUBSCRIPTION_CURRENCY}`,
            }),
          },
        ],
        additionalCollaboratorSeatPrice: ADDITIONAL_COLLABORATOR_SEAT_PRICE,
        additionalVehicleSlotPrice: ADDITIONAL_VEHICLE_SLOT_PRICE,
        basePrice: 19.99,
        features: [
          {
            iconProps: { flip: "horizontal", icon: faCars },
            title: t("tier.features.includedVehicles", {
              count: ENTHUSIAST_SLOTS_LIMIT,
            }),
          },
          {
            iconProps: { icon: faUserGroup },
            title: t("tier.features.collaboration", {
              count: ALLOWANCE_BY_TIER_VALUE[TierLevel.PRO]?.seats,
            }),
          },
          {
            description: t("tier.features.encryption.description"),
            title: t("tier.features.encryption"),
          },
          {
            title: t("tier.features.vehicleLimit", { count: 0 }),
          },
        ],
        id: "pro",
        level: TierLevel.PRO,
        productId: import.meta.env.REACT_APP_STRIPE_PRO_PRODUCT_ID,
        title: t("tier.pro"),
      },
    ],
    [t],
  );
};
